import { Selector } from "react-redux";
import { createSelector } from "reselect";

import { createGetSelectedComponents, createGetSelectedLinks, IEntity, IPicassoState } from "@multiweb/picasso";
import { IAppState, TypedObject } from "@multiweb/types/react";
import { WorkflowEditorMode } from "./enums";
import {
    ActivityPropertyContainers,
    GeneralPropertyContainer,
    IActivity,
    IHostInstanceState,
    IParticipant,
    ISet,
    ITransition,
    IVariable,
    IWorkflowEditorState,
    TransitionPropertyContainers,
    WfMessageWindowState,
    WorkflowEditorProps,
} from "./types";
import { ActivityType } from "./workflowModel";

const getModel: Selector<IAppState, IWorkflowEditorState> = (state) => state.workflowEditor;

/**
 * Vrací instanci stavu Editoru Workflow dle daného @param hostKey
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getWorkflowEditorInstance: Selector<IAppState, IHostInstanceState, string> = (state, hostKey) => {
    const instanceState = getModel(state).instances[hostKey];

    if (!instanceState)
        throw new Error(`[WorkflowEditor] - WorkflowEditor with key ${hostKey} is not initialized.`);

    return instanceState;
};

/**
 * Vrací instanci stavu Picasa.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getPicassoState: Selector<IAppState, IPicassoState, string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).picasso;

/** Vytváří selector vracejcí označené workflow elementy (propojení/aktivity) */
export const createGetSelectedWfElement: () => Selector<IAppState, IEntity | null, string> = () => {
    const getSelectedComponents = createGetSelectedComponents();
    const getSelectedLinks = createGetSelectedLinks();

    return (state, hostKey) => {
        const picassoState = getPicassoState(state, hostKey);

        const selectedComponents = getSelectedComponents(picassoState);
        const selectedLinks = getSelectedLinks(picassoState);

        if (selectedComponents.length > 0)
            return selectedComponents[0];

        if (selectedLinks.length > 0)
            return selectedLinks[0];

        return null;
    };
};

/**
 * Vrací aktuální mód, ve kterém je spuštěn editor WF.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
const getWorkflowEditorMode: Selector<IAppState, WorkflowEditorMode, string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).wfEditorMode;

/**
 * Vrací informaci o tom, zdali je aktivován režim zobrazení stavu WF.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const isStatusViewEnabled: Selector<IAppState, boolean, string> = (state, hostKey) => getWorkflowEditorMode(state, hostKey) === WorkflowEditorMode.StatusViewer;

/**
 * Vrací informaci o tom, zdali jse workflow readonly (nelze uložit).
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 * @returns
 */
export const isWorkflowReadOnly: Selector<IAppState, boolean, string> = (state, hostKey) => {
    const wfEditorMode = getWorkflowEditorMode(state, hostKey);
    return (wfEditorMode & (WorkflowEditorMode.StatusViewer | WorkflowEditorMode.Viewer)) !== 0;
};

/**
 * Vrací stav dialogového okna.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
const getWfMessageWindowState: Selector<IAppState, WfMessageWindowState, string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).wfMessageWindowState;

/**
 * Vrací informaci, zdali je dialogové okno viditelné či nikoliv.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const isWfMessageWindowVisible: Selector<IAppState, boolean, string> = (state, hostKey) => getWfMessageWindowState(state, hostKey).isVisible;

/**
 * Vrací ID akce, které si dialogové okno týká.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getWfMessageWindowActionId: Selector<IAppState, string | null, string> = (state, hostKey) => getWfMessageWindowState(state, hostKey).actionId;

/**
 * Vrací lokalizační klíč obsahu dialogového okna.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getWfMessageWindowContentLocKey: Selector<IAppState, string | null, string> = (state, hostKey) => getWfMessageWindowState(state, hostKey).contentLocKey;

/**
 * Vrací lokalizační klíč primary buttonu dialogového okna.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getWfMessageWindowPrimaryButtonLocKey: Selector<IAppState, string | null, string> = (state, hostKey) => getWfMessageWindowState(state, hostKey).primaryButtonLocKey;

/**
 * Vrací lokalizační klíč secondary buttonu dialogového okna.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getWfMessageWindowSecondaryButtonLocKey: Selector<IAppState, string | null, string> = (state, hostKey) => getWfMessageWindowState(state, hostKey).secondaryButtonLocKey;

/**
 * Vrací informaci o tom, zdali WF bylo nějak editováno.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const isTouched: Selector<IAppState, boolean, string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).touched;

/**
 * Vrací pole všech sad ve workflow.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getSets: Selector<IAppState, TypedObject<ISet>, string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).sets;

/**
 * Vrací seřazené pole id sad.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getOrderedSetsIds: Selector<IAppState, readonly string[], string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).orderedSetsIds;

/**
 * Vytvoří selektor, který vrací seřazené pole sad. Jako parametr vyžaduje klíč instance editoru WF.
 */
export const createGetOrderedSets = () => createSelector<IAppState, WorkflowEditorProps["hostKey"], ReturnType<typeof getSets>, ReturnType<typeof getOrderedSetsIds>, ReadonlyArray<ISet>>(
    getSets,
    getOrderedSetsIds,
    (sets, orderedSetsIds) => orderedSetsIds.map((setId) => sets[setId]),
);

/**
 * Vrací zvolenou sadu.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getSelectedSet: Selector<IAppState, ISet, string> = (state, hostKey) => {
    const selectedSetId = getSelectedSetId(state, hostKey);
    return getSetById(state, { hostKey, setId: selectedSetId });
};

/**
 * Vrací id zvolené sady.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getSelectedSetId: Selector<IAppState, string, string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).selectedSetId;

/**
 * Vrací index zvolené sady. Pokud se sada nenalezne, vrací -1.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getSelectedSetIndex: Selector<IAppState, number, string> = (state, hostKey) => {
    const orderedSetsIds = getOrderedSetsIds(state, hostKey);
    const selectedSetId = getSelectedSetId(state, hostKey);
    return orderedSetsIds.findIndex(setId => setId === selectedSetId);
};

/**
 * Vrací sadu podle poskytnutého id.
 * @param state
 * @param param1 Klíč instance Editoru WF a id sady, která se má získat.
 */
export const getSetById: Selector<IAppState, ISet, { hostKey: string; setId: string }> = (state, { hostKey, setId }) => {
    const sets = getSets(state, hostKey);
    return sets[setId];
};

/**
 * Vrací seznam všech aktivit zvolené sady.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getActivities: Selector<IAppState, readonly IActivity[], string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).activities;

/**
 * Vrací aktivitu podle daného {@link picassoIndex} ze zvolené sady.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 * @param picassoIndex Index aktvity ve stavu Picassa.
 */
export const getActivityByIndex = (state: IAppState, hostKey: string, picassoIndex: number) => getActivities(state, hostKey)[picassoIndex - 1]; // minus proto, že v picassu je sada mezi komponentami (index === 0)

/**
 * Vytváří selector vracející seznam označených aktivit. Vyžaduje klíč instance editoru WF.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const createGetSelectedActivities: () => Selector<IAppState, readonly IActivity[], string> = () => {
    const getSelectedComponents = createGetSelectedComponents();

    return (state, hostKey) => {
        const picassoState = getPicassoState(state, hostKey);
        const selectedComponents = getSelectedComponents(picassoState);
        return selectedComponents.map(({ index }) => getActivityByIndex(state, hostKey, index));
    };
};

/** Vrací na-cache-ovaný selector vracející zvolené aktivity. */
export const getSelectedActivities = createGetSelectedActivities();

/**
 * Vrací informaci o tom, zdali je označena pouze startovací aktivita.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const isOnlyStartActivitySelected: Selector<IAppState, boolean, string> = (state, hostKey) => {
    const selectedActivities = getSelectedActivities(state, hostKey);

    if (selectedActivities.length !== 1)
        return false;

    return selectedActivities[0].type === ActivityType.start;
};

/**
 * Vrací seznam všech propojení zvolené sady.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getTransitions: Selector<IAppState, readonly ITransition[], string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).transitions;

/**
 * Vrací propojení podle daného {@link picassoIndex} ze zvolené sady.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 * @param picassoIndex Index aktvity ve stavu Picassa.
 */
export const getTransitionByIndex = (state: IAppState, hostKey: string, picassoIndex: number) => getTransitions(state, hostKey)[picassoIndex];

/**
 * Vytváří selector vracející seznam označených propojení. Vyžaduje klíč instance editoru WF.
 */
export const createGetSelectedTransitions: () => Selector<IAppState, readonly ITransition[], string> = () => {
    const getSelectedLinks = createGetSelectedLinks();

    return (state, hostKey) => {
        const picassoState = getPicassoState(state, hostKey);
        const selectedLinks = getSelectedLinks(picassoState);
        return selectedLinks.map(({ index }) => getTransitionByIndex(state, hostKey, index));
    };
};

/**
 * Vrací stav otevřenosti pro daný @activityContainer aktivity.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 * @param activityContainer Typ kontejneru
 */
export const getActivityPanelStateContainer = (state: IAppState, hostKey: string, activityContainer: ActivityPropertyContainers) => getWorkflowEditorInstance(state, hostKey).activityPropertyPanelState[activityContainer];

/**
 * Vrací stav otevřenosti pro daný @activityContainer propojení.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 * @param transitionContainer Typ kontejneru
 */
export const getTransitionPanelStateContainer = (state: IAppState, hostKey: string, transitionContainer: TransitionPropertyContainers) => getWorkflowEditorInstance(state, hostKey).transitionPropertyPanelState[transitionContainer];

/**
 * Vrací stav otevřenosti pro daný @generalContainer workflow.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 * @param generalContainer Typ kontejneru
 */
export const getGeneralPanelStateContainer = (state: IAppState, hostKey: string, generalContainer: GeneralPropertyContainer) => getWorkflowEditorInstance(state, hostKey).generalPropertyPanelState[generalContainer];

/**
 * Vrací seznam definičních hráčů.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getDefinitionParticipants: Selector<IAppState, readonly IParticipant[], string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).definitionParticipants;

/**
 * Vrací informaci o tom, zdali jsou edit dialogy skryty či nikoliv.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const isEditDialogHidden: Selector<IAppState, boolean, string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).isEditDialogHidden;

/**
 * Vrací seznam proměnných. Funkční pouze v status viewer modu.
 * @param state
 * @param hostKey Klíč instance Editoru Workflow.
 */
export const getVariables: Selector<IAppState, readonly IVariable[], string> = (state, hostKey) => getWorkflowEditorInstance(state, hostKey).variables;
